<template>
  <login-component-layout component="SignUpForm">
    <v-form v-model="isFormValid" @submit.prevent="submit">
      <v-row class="mt-n4">
        <v-col cols="6" md="4">
          <v-radio-group v-model="isCompany" row>
            <v-radio class="ml-3" :label="i18n['private']" :value="false" />
          </v-radio-group>
        </v-col>
        <v-col cols="6" md="8">
          <v-radio-group v-model="isCompany" row>
            <v-radio :label="i18n['companyUser']" :value="true" data-test-form-iscompany />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-text-field
        v-if="createUserDto.isCompany"
        :label="i18n['company']"
        :rules="requiredRule"
        v-model="createUserDto.company"
        id="company"
        outlined
      />
      <v-text-field
        :label="i18n['firstNameLabel']"
        :rules="requiredRule"
        v-model="createUserDto.firstName"
        id="firstName"
        outlined
      />
      <v-text-field
        :label="i18n['lastNameLabel']"
        :rules="requiredRule"
        v-model="createUserDto.lastName"
        id="lastName"
        outlined
      />
      <login-component-input-mail />
      <login-component-input-password
        suffix="New"
        :rule="passwordNewRule"
        :customLabel="$t('components.login.LoginComponentInputPassword.password')"
      />
      <login-component-input-password suffix="Repeat" :rule="passwordRepeatRule" />
      <login-component-password-requirement-list :password="passwordNew" :setPass="setPass" />
      <v-checkbox
        :rules="[(v) => !!v || this.$t('report.thgClosing.readPrivacy')]"
        v-model="isTermsAccepted"
        ref="privacy"
        :label="$t('report.thgClosing.confirmationPrivacy')"
        class="padded"
        data-test-privacy-checkbox
      />
      <v-checkbox
        v-model="isMarketingContactAllowed"
        :label="$t('report.thgClosing.marketing')"
        class="padded"
        data-test-marketing-checkbox
      />

      <login-component-message :failText="failText" :successText="successText" />
      <login-component-submit-button :isFormValid="!isButtonDisabled" :isLoading="isLoading" component="SignUpForm" />
      <login-component-navigation-button to="/login" />
    </v-form>
  </login-component-layout>
</template>

<script lang="ts">
import { requiredRule } from "@/lib/rules/requiredRule";
import LoginErrorMixin from "@/mixins/LoginErrorMixin.vue";
import LoginMixin from "@/mixins/LoginMixin.vue";
import LoginNewPasswordMixin from "@/mixins/LoginNewPasswordMixin.vue";
import { ThgCreateUserDtoGen, ThgSignUpUserDtoGen } from "@/services/thg/v1/data-contracts";
import { LoginModule } from "@/store/modules/login.store";
import { Component, Mixins } from "vue-property-decorator";
import LoginComponentInputMail from "./LoginComponentInputMail.vue";
import LoginComponentInputPassword from "./LoginComponentInputPassword.vue";
import LoginComponentLayout from "./LoginComponentLayout.vue";
import LoginComponentMessage from "./LoginComponentMessage.vue";
import LoginComponentNavigationButton from "./LoginComponentNavigationButton.vue";
import LoginComponentPasswordRequirementList from "./LoginComponentPasswordRequirementList.vue";
import LoginComponentSubmitButton from "./LoginComponentSubmitButton.vue";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";

@Component({
  components: {
    LoginComponentLayout,
    LoginComponentInputMail,
    LoginComponentInputPassword,
    LoginComponentMessage,
    LoginComponentSubmitButton,
    LoginComponentPasswordRequirementList,
    LoginComponentNavigationButton
  }
})
export default class SignUpForm extends Mixins(LoginMixin, LoginNewPasswordMixin, LoginErrorMixin) {
  createUserDto: ThgCreateUserDtoGen = {
    firstName: "",
    lastName: "",
    userName: "",
    company: "",
    isMarketingOptIn: false,
    isTermsAccepted: false,
    isCompany: false,
    isTaxDeductible: false
  };

  async submit() {
    try {
      this.createUserDto.userName = this.mail;
      this.createUserDto.contact = {
        email: this.mail,
        phone: ""
      };
      this.createUserDto.language = this.$i18n.locale as LanguageCodeEnum;

      const signUpDto: ThgSignUpUserDtoGen = {
        password: this.passwordRepeat,
        createUserDto: this.createUserDto
      };
      this.isLoading = true;
      await LoginModule.signUp(signUpDto);

      // set the password to enable auto login after code confirmed
      LoginModule.setPasswordBySuffix({ password: this.passwordRepeat, suffix: "" });

      this.isFormSubmitted = true;
      await this.handleSuccess("/login/confirmmail");
    } catch (error) {
      this.handleFail(error);
    } finally {
      this.isLoading = false;
    }
  }

  get requiredRule() {
    return [requiredRule()];
  }

  /**
   * Get input label
   */
  get i18n() {
    return this.$t("components.login.SignUpForm");
  }

  get isTermsAccepted(): boolean {
    return this.createUserDto.isTermsAccepted || false;
  }

  set isTermsAccepted(bool: boolean) {
    this.createUserDto.isTermsAccepted = bool;
  }

  get isMarketingContactAllowed(): boolean {
    return this.createUserDto.isMarketingOptIn || false;
  }

  set isMarketingContactAllowed(bool: boolean) {
    this.createUserDto.isMarketingOptIn = bool;
  }

  get isCompany(): boolean {
    return this.createUserDto.isCompany ?? false;
  }

  set isCompany(bool: boolean) {
    this.createUserDto.isCompany = bool;
    this.createUserDto.isTaxDeductible = bool;
    if (!this.isCompany) {
      this.createUserDto.company = "";
    }
  }
}
</script>
